<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import CopyButton from '@/components/CopyButton.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import GbButton from '@/components/GbButton.vue'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import type { WikiTextStrategy } from '@/components/wikitext/wiki_text_types'
import ResourceLanguageDropdown from '@/resources/ResourceLanguageDropdown.vue'
import ResourceListContainer from '@/resources/ResourceListContainer.vue'
import { hasNonEnglishLanguage, resourceLanguages } from '@/resources/resource_list'
import type { Resource } from '@/resources/resource_types'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import AccordionObjective from './AccordionObjective.vue'
import { objectiveContentTitle, type AnchorObjective, type ObjectiveEdit } from './anchor_objective_types'
import type { ObjectiveLevel } from './objective_levels'
import { LOADING_ROW_COUNT, LOADING_ROW_WIDTHS, objectiveEdit } from './objective_list'

const ALL_OBJECTIVES_LEVEL = 'all'

interface EditAllToggle {
  text: string
  ariaLabel: string
  icon: string
  theme: string
}

const START_EDITING_ALL: EditAllToggle = { text: 'Edit', ariaLabel: 'Edit all objectives', icon: 'edit', theme: 'bluePrimary' }
const FINISH_EDITING_ALL: EditAllToggle = { text: 'Done', ariaLabel: 'Done, save all objectives', icon: 'save', theme: 'greenPrimary' }

const props = withDefaults(
  defineProps<{
    objectives: AnchorObjective[]
    objectiveTexts: string[]
    pageUuid?: string
    isEditable?: boolean
    isReadOnly?: boolean
    readOnlyHint?: string
    isLocked?: boolean
    isFetching?: boolean
    strategies?: Record<string, WikiTextStrategy>
    resourcesByObjectId?: Record<string, Resource[]>
    isFetchingResourcesByObjectId?: Record<string, boolean>
    levelsWithGeneratedAssessments?: ObjectiveLevel[]
  }>(),
  {
    pageUuid: '',
    isEditable: false,
    isReadOnly: false,
    readOnlyHint: undefined,
    isLocked: false,
    isFetching: false,
    strategies: () => ({}),
    resourcesByObjectId: () => ({}),
    isFetchingResourcesByObjectId: () => ({}),
    levelsWithGeneratedAssessments: () => []
  }
)

const selectedLanguages = defineModel<Record<string, string>>('selectedLanguages', { default: () => ({}) })

const emit = defineEmits<{
  'update:objective': [edit: ObjectiveEdit]
}>()

// Generated assessments exist only on saved pages, which render their tiles here; the page
// also lists the levels it fills, since a slot cannot report whether it renders anything.
defineSlots<{
  generatedAssessments?(props: { objective: AnchorObjective, index: number }): unknown
}>()

const analytics = getAnalyticsTracker()

const isEditingAll = ref(false)
const allObjectivesToolbarRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)

const hasResources = computed(() => props.objectives.some((objective) => Boolean(objective.resources?.length)))
const isTextEdited = computed(() => props.objectives.some((objective, index) => objective.text !== props.objectiveTexts[index]))
const allObjectivesCopyText = computed(() => props.objectiveTexts.map(wikiToPlainText).join('\n\n'))
// Done stays even when read-only, so an edit already open can always be finished.
const isEditAllToggleShown = computed(() => props.isEditable && (isEditingAll.value || !props.isReadOnly))
const editAllToggle = computed(() => (isEditingAll.value ? FINISH_EDITING_ALL : START_EDITING_ALL))
// With no resources, the objectives read as one block that is edited and copied as a whole.
const sharedEditMode = computed(() => (hasResources.value ? undefined : isEditingAll.value))
const lastIndex = computed(() => props.objectives.length - 1)

function objectiveKey(objective: AnchorObjective): string {
  return `${objective.level}-${objective.uuid ?? ''}`
}

function objectiveResources(objective: AnchorObjective): Resource[] {
  return props.resourcesByObjectId[objective.uuid ?? ''] ?? []
}

function isFetchingResources(objective: AnchorObjective): boolean {
  return props.isFetchingResourcesByObjectId[objective.uuid ?? ''] ?? false
}

function isMultilingual(objective: AnchorObjective): boolean {
  return Boolean(objective.uuid) && hasNonEnglishLanguage(objectiveResources(objective))
}

function hasResourceStrip(objective: AnchorObjective): boolean {
  const hasCuratedResources = Boolean(objective.resources?.length)
  return hasCuratedResources || props.levelsWithGeneratedAssessments.includes(objective.level)
}

function isEditorShown(index: number): boolean {
  return props.isEditable && Boolean(props.objectiveTexts[index])
}

function isReadOnlyTextShown(objective: AnchorObjective): boolean {
  return !props.isEditable && Boolean(objective.text)
}

function isDividerShown(index: number): boolean {
  return hasResources.value && index !== lastIndex.value
}

function selectedLanguage(objective: AnchorObjective): string {
  return selectedLanguages.value[objective.uuid ?? ''] ?? ''
}

function selectLanguage(objective: AnchorObjective, language: string) {
  selectedLanguages.value = { ...selectedLanguages.value, [objective.uuid ?? '']: language }
}

function editObjective(index: number, text: string) {
  emit('update:objective', objectiveEdit(props.objectives, index, text, !hasResources.value))
}

// The objectives' own text is not focusable (it holds links), so each gets a screen-reader Edit
// that opens them all and lands in its own textarea.
function isObjectiveEditButtonShown(index: number): boolean {
  return sharedEditMode.value === false && isEditAllToggleShown.value && isEditorShown(index)
}

function editAllFromObjective(index: number) {
  setEditingAll(true)
  nextTick(() => listRef.value?.querySelector<HTMLTextAreaElement>(`[data-objective-index="${index}"] textarea`)?.focus())
}

// Wait a tick so the page has applied the last committed edit before it is compared.
function setEditingAll(isEditing: boolean) {
  if (isEditingAll.value === isEditing) return
  isEditingAll.value = isEditing
  if (isEditing) {
    analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.ACTIVATE_EDIT, { obj_level: ALL_OBJECTIVES_LEVEL })
    return
  }
  nextTick(() => {
    analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.DEACTIVATE_EDIT, {
      obj_level: ALL_OBJECTIVES_LEVEL,
      is_text_edited: isTextEdited.value
    })
    returnFocusIfLost()
  })
}

// Finishing closes the textarea that held focus, so focus would otherwise drop to the page.
function returnFocusIfLost() {
  const isFocusLost = !document.activeElement || document.activeElement === document.body
  if (isFocusLost) allObjectivesToolbarRef.value?.querySelector('button')?.focus()
}
</script>

<template>
  <div ref="listRef" class="px-12 pb-3" data-testid="accordion-objectives">
    <div v-if="isFetching" data-testid="accordion-objectives-loading">
      <LoadingSkeletonRow v-for="row in LOADING_ROW_COUNT" :key="row" :possible-widths="LOADING_ROW_WIDTHS" />
    </div>
    <div v-else role="list">
      <AccordionObjective
        v-for="(objective, index) in objectives"
        :key="objectiveKey(objective)"
        role="listitem"
        :level="objective.level"
        :title="objective.title"
        :data-objective-index="index"
      >
        <CopyTrackWrapper type="objective" :level="objective.level" gate-copy>
          <button
            v-if="isObjectiveEditButtonShown(index)"
            type="button"
            class="sr-only focus:not-sr-only"
            :aria-label="`Edit ${objectiveContentTitle(objective)} statement`"
            data-testid="edit-objective"
            @click="editAllFromObjective(index)"
          >
            Edit
          </button>
          <WikiTextEditor
            v-if="isEditorShown(index)"
            format="markdown"
            :wiki-text="objectiveTexts[index]"
            :label="`${objectiveContentTitle(objective)} statement`"
            :strategies="strategies"
            :read-only="isReadOnly"
            :read-only-hint="readOnlyHint"
            :hide-controls="!hasResources"
            :edit-mode="sharedEditMode"
            :evt-data="{ obj_level: objective.level }"
            copy-tooltip-text="Objective Copied"
            @update:wiki-text="editObjective(index, $event)"
            @update:edit-mode="setEditingAll"
          >
            <template v-if="isMultilingual(objective)" #bottomRightContent>
              <ResourceLanguageDropdown
                :languages="resourceLanguages(objectiveResources(objective))"
                :model-value="selectedLanguage(objective)"
                @update:model-value="selectLanguage(objective, $event)"
              />
            </template>
          </WikiTextEditor>
          <template v-if="isReadOnlyTextShown(objective)">
            <WikiText :text="objective.text" :strategies="strategies" :locked="isLocked" />
            <ResourceLanguageDropdown
              v-if="isMultilingual(objective)"
              :languages="resourceLanguages(objectiveResources(objective))"
              :model-value="selectedLanguage(objective)"
              @update:model-value="selectLanguage(objective, $event)"
            />
          </template>
        </CopyTrackWrapper>
        <ResourceListContainer
          v-if="hasResourceStrip(objective)"
          :resources="objectiveResources(objective)"
          :is-fetching-resources="isFetchingResources(objective)"
          :selected-language="selectedLanguage(objective)"
          :evt-data="{ obj_level: objective.level }"
          :is-tiled="true"
        >
          <slot name="generatedAssessments" :objective="objective" :index="index" />
        </ResourceListContainer>
        <hr v-if="isDividerShown(index)" class="border-gray-300" data-testid="accordion-objective-divider">
      </AccordionObjective>
    </div>

    <CopyTrackWrapper
      v-if="!hasResources"
      type="objective"
      :level="ALL_OBJECTIVES_LEVEL"
      :title="ALL_OBJECTIVES_LEVEL"
      :uuid="pageUuid"
      gate-copy
    >
      <div ref="allObjectivesToolbarRef" class="flex items-center gap-1" data-shared-edit-control>
        <GbButton
          v-if="isEditAllToggleShown"
          :gb-theme="editAllToggle.theme"
          size="xs"
          :aria-label="editAllToggle.ariaLabel"
          data-testid="edit-all-objectives"
          @mousedown.prevent
          @click="setEditingAll(!isEditingAll)"
        >
          <span class="material-icons-round pr-1 text-lg" aria-hidden="true">{{ editAllToggle.icon }}</span>{{ editAllToggle.text }}
        </GbButton>
        <CopyButton
          :string-to-copy="allObjectivesCopyText"
          tooltip-text="Copied All Objectives"
          button-text="Copy"
          button-theme="grayOutlineSecondary"
          custom-class="bg-white"
          size="xs"
          icon-class="material-icons-outlined"
          aria-label="Copy all objectives"
          auto-hide-tooltip
        />
      </div>
    </CopyTrackWrapper>
  </div>
</template>
